// =============================================================================
// FireISP 5.0 — Work Orders (§12)
// =============================================================================
// List, create, and manage field work orders.  Supports status transitions
// (dispatch → start → complete/cancel) and materials sub-resource.
// =============================================================================

import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, authedFetch } from '@/api/client';
import { styles, modalStyles } from './crudStyles';
import { ClientPicker } from '@/components/ClientPicker';
import { useTableSort, SortableTh } from '@/components/SortableTh';
import { MarkdownView } from '@/components/MarkdownView';
import {
  CommunicationOptInFields,
  type CommunicationContacts,
  type CommunicationOptIns,
  type SigningPrivacyNotice,
} from '@/components/CommunicationOptInFields';
import { useAuth } from '@/auth/AuthContext';
import { can } from '@/auth/permissions';
import {
  MxSandboxDocumentBanner,
  type MxContractEnvironment,
} from '@/components/MxContractEnvironment';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface WorkOrder {
  id: number;
  ticket_id: number | null;
  assigned_to: number | null;
  status: string;
  priority: string | null;
  title: string;
  description: string | null;
  scheduled_at: string | null;
  completed_at: string | null;
  organization_id: number;
  created_at: string;
  // Target links (work_orders is the single field-work table since migration 363)
  client_id: number | null;
  site_id: number | null;
  device_id: number | null;
  contract_id: number | null;
  service_order_id: number | null;
  work_type: string | null;
  client_name: string | null;
  site_name: string | null;
  device_name: string | null;
  assigned_first: string | null;
  assigned_last: string | null;
}

interface WorkOrderBody {
  title: string;
  description?: string;
  ticket_id?: number;
  assigned_to?: number;
  status?: string;
  priority?: string;
  scheduled_at?: string;
  client_id?: number;
  site_id?: number;
  device_id?: number;
  contract_id?: number;
  service_order_id?: number;
  work_type?: string;
}

// PATCH accepts a subset of fields, and (unlike create) allows explicit null to
// clear description / schedule / target links / assignee.
interface WorkOrderPatchBody {
  title?: string;
  description?: string | null;
  status?: string;
  priority?: string;
  work_type?: string;
  scheduled_at?: string | null;
  client_id?: number | null;
  site_id?: number | null;
  device_id?: number | null;
  assigned_to?: number | null;
  // Install-acceptance readings (migration 445) — the backend refuses to
  // complete a contract-linked installation WO without one of these or a waive.
  acceptance_signal_dbm?: number;
  acceptance_link_mbps?: number;
  acceptance_rx_dbm?: number;
  acceptance_waived?: boolean;
  acceptance_notes?: string;
}

interface Option { id: number; name: string }

interface UserOption { id: number; first_name: string; last_name: string }

interface WorkOrderMaterial {
  id: number;
  item_name: string;
  quantity: number;
  unit: string | null;
  unit_cost: number | null;
  notes: string | null;
}

interface WorkOrderMaterialBody {
  item_name: string;
  quantity: number;
  unit?: string;
  unit_cost?: number;
  notes?: string;
}

interface ListResponse<T> {
  data: T[];
  meta: { total: number; page: number; limit: number };
}

const PAGE_SIZE = 25;
const STATUSES = ['pending', 'assigned', 'in_progress', 'completed', 'cancelled'];
const WORK_TYPES = ['installation', 'maintenance', 'repair', 'survey', 'other'];
const PRIORITIES = ['low', 'medium', 'high', 'critical'];

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

async function fetchWorkOrders(page: number, statusFilter: string, orderBy: string, order: string): Promise<ListResponse<WorkOrder>> {
  const query: Record<string, string | number> = { page, limit: PAGE_SIZE, order_by: orderBy, order };
  if (statusFilter) query.status = statusFilter;
  const res = await api.GET('/work-orders' as never, {
    params: { query: query as never },
  } as never);
  if ((res as { error?: unknown }).error) throw new Error('Failed to load work orders');
  return (res as { data: unknown }).data as unknown as ListResponse<WorkOrder>;
}

async function fetchOptions(pathname: '/sites' | '/devices'): Promise<Option[]> {
  const res = await api.GET(pathname as never, { params: { query: { limit: 200 } as never } } as never);
  if ((res as { error?: unknown }).error) return [];
  return (((res as { data: unknown }).data as { data: Option[] }).data) ?? [];
}

// Only users authorized to work with work orders (server-side: holders of
// work_orders.update) are assignable, so the picker is populated from the
// dedicated endpoint rather than the full /users list.
async function fetchAssignableUsers(): Promise<UserOption[]> {
  const res = await api.GET('/work-orders/assignable-users' as never, {} as never);
  if ((res as { error?: unknown }).error) return [];
  return (((res as { data: unknown }).data as { data: UserOption[] }).data) ?? [];
}

// Surface the server's error message (e.g. an unauthorized-assignee 422) instead
// of a generic string, so form validation failures are actionable.
async function errorMessage(resp: Response, fallback: string): Promise<string> {
  try {
    const j = await resp.json() as { error?: string };
    if (j && typeof j.error === 'string') return j.error;
  } catch { /* non-JSON / empty body */ }
  return fallback;
}

async function createWorkOrder(body: WorkOrderBody): Promise<WorkOrder> {
  const resp = await authedFetch('/api/v1/work-orders', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!resp.ok) throw new Error(await errorMessage(resp, 'Failed to create work order'));
  const json = await resp.json() as { data: WorkOrder };
  return json.data;
}

async function patchWorkOrder(id: number, body: WorkOrderPatchBody): Promise<void> {
  const resp = await authedFetch(`/api/v1/work-orders/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!resp.ok) throw new Error(await errorMessage(resp, 'Failed to update work order'));
}

async function fetchMaterials(workOrderId: number): Promise<WorkOrderMaterial[]> {
  const res = await api.GET('/work-orders/{id}/materials' as never, {
    params: { path: { id: workOrderId } as never },
  } as never);
  if ((res as { error?: unknown }).error) throw new Error('Failed to load materials');
  return ((res as { data: unknown }).data as { data: WorkOrderMaterial[] }).data;
}

async function addMaterial(workOrderId: number, body: WorkOrderMaterialBody): Promise<void> {
  const resp = await authedFetch(`/api/v1/work-orders/${workOrderId}/materials`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!resp.ok) throw new Error('Failed to add material');
}

async function removeMaterial(workOrderId: number, materialId: number): Promise<void> {
  const resp = await authedFetch(`/api/v1/work-orders/${workOrderId}/materials/${materialId}`, {
    method: 'DELETE',
  });
  if (!resp.ok && resp.status !== 204) throw new Error('Failed to remove material');
}

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, { bg: string; color: string }> = {
    pending:     { bg: '#fef3c7', color: '#92400e' },
    assigned:    { bg: '#dbeafe', color: '#1e40af' },
    in_progress: { bg: '#ede9fe', color: '#5b21b6' },
    completed:   { bg: '#d1fae5', color: '#065f46' },
    cancelled:   { bg: '#f3f4f6', color: '#6b7280' },
  };
  const s = colors[status] ?? { bg: '#f3f4f6', color: '#374151' };
  return (
    <span style={{
      background: s.bg,
      color: s.color,
      padding: '2px 8px',
      borderRadius: 12,
      fontSize: '0.72rem',
      fontWeight: 600,
      textTransform: 'capitalize',
    }}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Materials panel (shown inline when a row is expanded)
// ---------------------------------------------------------------------------

function MaterialsPanel({ workOrderId }: { workOrderId: number }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState<Partial<WorkOrderMaterialBody>>({ quantity: 1 });
  const [addErr, setAddErr] = useState('');

  const materialsQ = useQuery({
    queryKey: ['workOrders', workOrderId, 'materials'],
    queryFn: () => fetchMaterials(workOrderId),
  });

  const addMut = useMutation({
    mutationFn: () => addMaterial(workOrderId, form as WorkOrderMaterialBody),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workOrders', workOrderId, 'materials'] });
      setShowAdd(false);
      setForm({ quantity: 1 });
    },
    onError: (e: unknown) => setAddErr((e as { message?: string })?.message ?? 'Failed'),
  });

  const removeMut = useMutation({
    mutationFn: (materialId: number) => removeMaterial(workOrderId, materialId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workOrders', workOrderId, 'materials'] }),
  });

  return (
    <div style={{ padding: '12px 16px', background: 'var(--bg-secondary)', borderTop: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <strong style={{ fontSize: '0.85rem' }}>{t('workOrders.materials')}</strong>
        <button style={styles.btnPrimary} onClick={() => setShowAdd(v => !v)}>
          {t('workOrders.addMaterial')}
        </button>
      </div>

      {showAdd && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
          <input
            style={styles.input}
            placeholder="Item name"
            value={form.item_name ?? ''}
            onChange={e => setForm(f => ({ ...f, item_name: e.target.value }))}
          />
          <input
            style={{ ...styles.input, width: 80 }}
            type="number"
            placeholder="Qty"
            value={form.quantity ?? 1}
            onChange={e => setForm(f => ({ ...f, quantity: Number(e.target.value) }))}
          />
          <input
            style={{ ...styles.input, width: 80 }}
            placeholder="Unit"
            value={form.unit ?? ''}
            onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}
          />
          <button
            style={styles.btnPrimary}
            disabled={!form.item_name || addMut.isPending}
            onClick={() => { setAddErr(''); addMut.mutate(); }}
          >
            {addMut.isPending ? t('common.saving') : t('common.save')}
          </button>
          {addErr && <span style={{ color: '#dc2626', fontSize: '0.8rem' }}>{addErr}</span>}
        </div>
      )}

      {materialsQ.isLoading ? (
        <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{t('common.loading')}</span>
      ) : (
        <table style={styles.table}>
          <tbody>
            {(materialsQ.data ?? []).map(m => (
              <tr key={m.id}>
                <td style={styles.td}>{m.item_name}</td>
                <td style={styles.td}>{m.quantity} {m.unit ?? ''}</td>
                <td style={styles.td}>{m.unit_cost !== null ? `$${m.unit_cost}` : '—'}</td>
                <td style={styles.td}>
                  <button
                    style={styles.btnDanger}
                    onClick={() => removeMut.mutate(m.id)}
                    disabled={removeMut.isPending}
                  >
                    {t('common.delete')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pickup checklist panel (Inventory Phase 3, migration 391) — shown inline
// for work_type='pickup' orders instead of MaterialsPanel. Lists outstanding
// RENTED equipment for the order's contract (sold devices never appear —
// they're the client's property) and lets a technician resolve each unit as
// returned-to-stock or damaged/RMA. The work order auto-completes on the
// backend once every listed unit is resolved.
// ---------------------------------------------------------------------------

interface PickupUnit {
  id: number;
  serial_number: string;
  item_name: string | null;
  sku: string | null;
  lifecycle_state: string;
}

async function fetchPickupItems(workOrderId: number): Promise<PickupUnit[]> {
  const res = await api.GET('/work-orders/{id}/pickup-items' as never, {
    params: { path: { id: workOrderId } as never },
  } as never);
  if ((res as { error?: unknown }).error) throw new Error('Failed to load pickup checklist');
  return ((res as { data: unknown }).data as { data: PickupUnit[] }).data ?? [];
}

async function resolvePickupUnit(workOrderId: number, cpeDeviceId: number, disposition: 'returned' | 'rma'): Promise<void> {
  const resp = await authedFetch(`/api/v1/work-orders/${workOrderId}/pickup-items`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cpe_device_id: cpeDeviceId, disposition }),
  });
  if (!resp.ok) throw new Error(await errorMessage(resp, 'Failed to resolve pickup item'));
}

function PickupChecklistPanel({ workOrderId }: { workOrderId: number }) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const itemsQ = useQuery({
    queryKey: ['workOrders', workOrderId, 'pickupItems'],
    queryFn: () => fetchPickupItems(workOrderId),
  });

  const resolveMut = useMutation({
    mutationFn: ({ cpeDeviceId, disposition }: { cpeDeviceId: number; disposition: 'returned' | 'rma' }) =>
      resolvePickupUnit(workOrderId, cpeDeviceId, disposition),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workOrders', workOrderId, 'pickupItems'] });
      // The work order may have just auto-completed — refresh the list/status too.
      qc.invalidateQueries({ queryKey: ['workOrders'] });
    },
  });

  return (
    <div style={{ padding: '12px 16px', background: 'var(--bg-secondary)', borderTop: '1px solid var(--border)' }}>
      <strong style={{ fontSize: '0.85rem' }}>{t('workOrders.pickup.title')}</strong>
      {itemsQ.isLoading ? (
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{t('common.loading')}</p>
      ) : (itemsQ.data ?? []).length === 0 ? (
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: 8 }}>{t('workOrders.pickup.none')}</p>
      ) : (
        <table style={{ ...styles.table, marginTop: 8 }}>
          <thead>
            <tr>
              <th style={styles.th}>{t('workOrders.pickup.serial')}</th>
              <th style={styles.th}>{t('workOrders.pickup.product')}</th>
              <th style={styles.th}>{t('common.actions', 'Actions')}</th>
            </tr>
          </thead>
          <tbody>
            {(itemsQ.data ?? []).map(unit => (
              <tr key={unit.id}>
                <td style={{ ...styles.td, fontFamily: 'monospace' }}>{unit.serial_number}</td>
                <td style={styles.td}>{unit.item_name ?? t('workOrders.none')}</td>
                <td style={styles.td}>
                  <button
                    style={{ ...styles.btnPrimary, marginRight: 4 }}
                    disabled={resolveMut.isPending}
                    onClick={() => resolveMut.mutate({ cpeDeviceId: unit.id, disposition: 'returned' })}
                  >
                    {t('workOrders.pickup.returned')}
                  </button>
                  <button
                    style={styles.btnDanger}
                    disabled={resolveMut.isPending}
                    onClick={() => resolveMut.mutate({ cpeDeviceId: unit.id, disposition: 'rma' })}
                  >
                    {t('workOrders.pickup.rma')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {resolveMut.isError && (
        <p style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: 8 }}>
          {(resolveMut.error as Error)?.message ?? t('common.error', 'Something went wrong')}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Legal documents (migration 447) — the panel the technician opens on-site.
// Pending documents block the WO transitions server-side; this is where the
// client reads the frozen text and signs on the device.
// ---------------------------------------------------------------------------
interface SignedDocSummary {
  id: number; template_type: string; title: string; status: string;
  signer_name: string | null; signed_at: string | null;
}

interface SignedDocDetail extends SignedDocSummary {
  rendered_body: string;
  mx_contract_environment?: MxContractEnvironment | null;
  communication_contacts?: CommunicationContacts;
  privacy_notice?: SigningPrivacyNotice | null;
  communication_choices_recorded?: boolean;
}

function capturesCommunicationChoices(templateType: string): boolean {
  return templateType === 'activation_contract' || templateType === 'service_acknowledgment';
}

function needsCommunicationChoices(document: SignedDocDetail): boolean {
  return capturesCommunicationChoices(document.template_type) && document.communication_choices_recorded !== true;
}

function SignatureCanvas({ onChange }: { onChange: (dataUrl: string | null) => void }) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const dirty = useRef(false);

  function pos(e: React.PointerEvent<HTMLCanvasElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (rect.width ? e.currentTarget.width / rect.width : 1),
      y: (e.clientY - rect.top) * (rect.height ? e.currentTarget.height / rect.height : 1),
    };
  }
  function start(e: React.PointerEvent<HTMLCanvasElement>) {
    drawing.current = true;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    e.currentTarget.setPointerCapture(e.pointerId);
  }
  function move(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = pos(e);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#111';
    ctx.lineTo(x, y);
    ctx.stroke();
    dirty.current = true;
  }
  function end() {
    drawing.current = false;
    if (dirty.current && canvasRef.current) onChange(canvasRef.current.toDataURL('image/png'));
  }
  function clear() {
    const c = canvasRef.current;
    const ctx = c?.getContext('2d');
    if (c && ctx) ctx.clearRect(0, 0, c.width, c.height);
    dirty.current = false;
    onChange(null);
  }

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={420}
        height={140}
        data-testid="signature-canvas"
        style={{ border: '1px dashed var(--border-color, #9ca3af)', borderRadius: 8, background: '#fff', touchAction: 'none', width: '100%', maxWidth: 420 }}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
      />
      <button type="button" style={{ ...styles.btnSecondary, marginTop: 4, padding: '3px 10px', fontSize: '0.78rem' }} onClick={clear}>
        {t('workOrders.documents.clearSignature')}
      </button>
    </div>
  );
}

function SignDocumentModal({ docId, onClose, onSigned }: {
  docId: number; onClose: () => void; onSigned: () => void;
}) {
  const { t } = useTranslation();
  const [signerName, setSignerName] = useState('');
  const [signature, setSignature] = useState<string | null>(null);
  const [communicationOptIns, setCommunicationOptIns] = useState<CommunicationOptIns>({
    email: false,
    sms: false,
    whatsapp: false,
  });
  const [communicationChoicesConfirmed, setCommunicationChoicesConfirmed] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const docQ = useQuery({
    queryKey: ['signed-document', docId],
    queryFn: async () => {
      const res = await (api.GET as unknown as (p: string, o: unknown) => Promise<{ data?: unknown; error?: unknown }>)(
        '/signed-documents/{id}', { params: { path: { id: docId } } },
      );
      if (res.error) throw new Error('Failed to load the document');
      return (res.data as { data: SignedDocDetail }).data;
    },
  });

  useEffect(() => {
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);

  async function submit() {
    if (!signerName.trim()) { setErr(t('workOrders.documents.signerRequired')); return; }
    if (!signature) { setErr(t('workOrders.documents.signatureRequired')); return; }
    const captureChoices = Boolean(docQ.data && needsCommunicationChoices(docQ.data));
    const privacyNotice = docQ.data?.privacy_notice;
    if (captureChoices && !privacyNotice) { setErr(t('communicationOptIn.privacyUnavailable')); return; }
    if (captureChoices && !communicationChoicesConfirmed) { setErr(t('communicationOptIn.reviewRequired')); return; }
    const body: Record<string, unknown> = {
      signer_name: signerName.trim(),
      signature_image: signature,
    };
    if (captureChoices && privacyNotice) {
      body.communication_opt_ins = communicationOptIns;
      body.communication_choices_confirmed = true;
      body.privacy_notice_version = privacyNotice.version;
      body.privacy_notice_hash = privacyNotice.hash;
    }
    setErr('');
    setBusy(true);
    try {
      const res = await (api.POST as unknown as (p: string, o: unknown) => Promise<{ error?: { error?: { message?: string } } }>)(
        '/signed-documents/{id}/sign',
        { params: { path: { id: docId } }, body },
      );
      if (res.error) throw new Error(res.error.error?.message || 'Failed to sign');
      onSigned();
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to sign');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={modalStyles.backdrop} onClick={onClose}>
      <div style={{ ...modalStyles.panel, maxWidth: 640 }} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={t('workOrders.documents.signTitle')}>
        <div style={modalStyles.header}>
          <h2 style={modalStyles.title}>{docQ.data?.title ?? t('workOrders.documents.signTitle')}</h2>
          <button
            type="button"
            style={{ ...modalStyles.closeBtn, minWidth: 44, minHeight: 44 }}
            onClick={onClose}
            aria-label={t('common.close')}
          >✕</button>
        </div>
        <div style={{ ...modalStyles.form, maxHeight: '70vh', overflowY: 'auto' }}>
          {docQ.isLoading && <p>{t('common.loading')}</p>}
          {docQ.isError && (
            <div>
              <p role="alert" style={{ color: '#ef4444', fontSize: '0.85rem' }}>
                {t('workOrders.documents.loadFailed')}
              </p>
              <div style={modalStyles.actions}>
                <button type="button" style={styles.btnSecondary} onClick={onClose}>{t('common.cancel')}</button>
                <button type="button" style={styles.btnPrimary} onClick={() => void docQ.refetch()}>
                  {t('common.retry')}
                </button>
              </div>
            </div>
          )}
          {docQ.data && (
            <>
              <MxSandboxDocumentBanner environment={docQ.data.mx_contract_environment} />
              <div style={{ border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 8, padding: '0.75rem', background: 'var(--bg-subtle, #f9fafb)' }}>
                <MarkdownView markdown={docQ.data.rendered_body} />
              </div>
              {docQ.data.status === 'signed' ? (
                <p style={{ margin: 0, fontSize: '0.85rem' }}>
                  ✅ {t('workOrders.documents.alreadySigned', { name: docQ.data.signer_name ?? '' })}
                </p>
              ) : (
                <>
                  <label style={modalStyles.label}>
                    {t('workOrders.documents.signerName')}
                    <input style={modalStyles.input} value={signerName} onChange={e => setSignerName(e.target.value)} maxLength={200} />
                  </label>
                  <label style={modalStyles.label}>{t('workOrders.documents.signHere')}</label>
                  <SignatureCanvas onChange={setSignature} />
                  {needsCommunicationChoices(docQ.data) ? (
                    <CommunicationOptInFields
                      contacts={docQ.data.communication_contacts ?? { email: false, phone: false }}
                      privacyNotice={docQ.data.privacy_notice ?? null}
                      value={communicationOptIns}
                      onChange={setCommunicationOptIns}
                      confirmed={communicationChoicesConfirmed}
                      onConfirmedChange={setCommunicationChoicesConfirmed}
                      disabled={busy}
                    />
                  ) : capturesCommunicationChoices(docQ.data.template_type) ? (
                    <p data-testid="communication-choices-recorded" style={{ color: 'var(--text-secondary)', fontSize: '0.84rem' }}>
                      {t('communicationOptIn.alreadyRecorded')}
                    </p>
                  ) : null}
                  {err && <p style={{ color: '#ef4444', fontSize: '0.85rem', margin: 0 }}>{err}</p>}
                  <div style={modalStyles.actions}>
                    <button type="button" style={styles.btnSecondary} onClick={onClose} disabled={busy}>{t('common.cancel')}</button>
                    <button
                      type="button"
                      style={styles.btnPrimary}
                      onClick={submit}
                      disabled={busy || (
                        needsCommunicationChoices(docQ.data)
                        && (!communicationChoicesConfirmed || !docQ.data.privacy_notice)
                      )}
                    >
                      {busy ? t('workOrders.documents.signing') : t('workOrders.documents.signButton')}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Install test window (migration 448) — bounded internet for on-site testing
// before formal activation. Pending contracts are otherwise DOWN.
// ---------------------------------------------------------------------------
interface WorkOrderContractState {
  status: string;
  connection_type?: string | null;
  test_window_expires_at: string | null;
  test_window_cleanup_pending?: boolean | number;
  mx_contract_environment?: MxContractEnvironment | null;
}

async function fetchWorkOrderContract(contractId: number): Promise<WorkOrderContractState> {
  const res = await (api.GET as unknown as (
    p: string,
    options: unknown,
  ) => Promise<{ data?: unknown; error?: unknown }>)(
    '/contracts/{id}', { params: { path: { id: contractId } } },
  );
  if (res.error) throw new Error('unavailable');
  return (res.data as { data: WorkOrderContractState }).data;
}

function TestWindowPanel({ workOrderId, contractId }: { workOrderId: number; contractId: number }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [err, setErr] = useState('');

  const contractQ = useQuery({
    queryKey: ['wo-test-window-contract', contractId],
    queryFn: () => fetchWorkOrderContract(contractId),
    retry: false,
    refetchInterval: (q) => {
      const current = q.state.data as {
        test_window_expires_at: string | null;
        test_window_cleanup_pending?: boolean | number;
      } | undefined;
      return current?.test_window_expires_at || current?.test_window_cleanup_pending ? 60_000 : false;
    },
  });

  const act = useMutation({
    mutationFn: async (action: 'start' | 'end') => {
      const resp = await authedFetch(`/api/v1/work-orders/${workOrderId}/test-window/${action}`, { method: 'POST' });
      if (!resp.ok) throw new Error(await errorMessage(resp, t('workOrders.testWindow.failed', 'Test window action failed')));
    },
    onSuccess: () => { setErr(''); void qc.invalidateQueries({ queryKey: ['wo-test-window-contract', contractId] }); },
    onError: (e: Error) => setErr(e.message),
  });

  if (contractQ.isError || contractQ.isLoading) return null;
  const c = contractQ.data;
  if (!c || c.status !== 'pending') return null; // activation owns the line from there

  const expiryMs = c.test_window_expires_at ? new Date(c.test_window_expires_at).getTime() : 0;
  const cleanupPending = Boolean(Number(c.test_window_cleanup_pending ?? 0));
  const systemControlled = c.connection_type == null
    || c.connection_type === 'pppoe'
    || c.connection_type === 'pppoe_dual';
  const windowOpen = Boolean(expiryMs > Date.now() && !cleanupPending);
  const shutdownPending = cleanupPending || Boolean(c.test_window_expires_at && !windowOpen);
  return (
    <div style={{ padding: '0.5rem 1rem 0.25rem' }}>
      <strong style={{ fontSize: '0.85rem' }}>{t('workOrders.testWindow.title')}</strong>
      <MxSandboxDocumentBanner environment={c.mx_contract_environment} />
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 6, fontSize: '0.82rem', flexWrap: 'wrap' }}>
        {!systemControlled ? (
          <span style={{ color: 'var(--text-secondary)' }}>{t('workOrders.testWindow.manualLine')}</span>
        ) : windowOpen ? (
          <>
            <span style={{ color: 'var(--accent, #16a34a)' }}>
              🟢 {t('workOrders.testWindow.openUntil', { time: (c.test_window_expires_at ?? '').slice(11, 16) })}
            </span>
            <button style={{ ...styles.btnSecondary, padding: '3px 12px', fontSize: '0.78rem' }}
              disabled={act.isPending} onClick={() => act.mutate('end')}>
              {t('workOrders.testWindow.end')}
            </button>
          </>
        ) : shutdownPending ? (
          <>
            <span style={{ color: '#b45309' }}>{t('workOrders.testWindow.cleanupPending')}</span>
            <button style={{ ...styles.btnSecondary, padding: '3px 12px', fontSize: '0.78rem' }}
              disabled={act.isPending} onClick={() => act.mutate('end')}>
              {t('workOrders.testWindow.retryShutdown')}
            </button>
          </>
        ) : (
          <>
            <span style={{ color: 'var(--text-secondary)' }}>{t('workOrders.testWindow.lineDown')}</span>
            <button style={{ ...styles.btnPrimary, padding: '3px 12px', fontSize: '0.78rem' }}
              disabled={act.isPending} onClick={() => act.mutate('start')}>
              {t('workOrders.testWindow.start')}
            </button>
          </>
        )}
        {err && <span style={{ color: '#991b1b' }}>{err}</span>}
      </div>
      <p style={{ margin: '4px 0 0', fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
        {t('workOrders.testWindow.hint')}
      </p>
      <a href={`/contracts/${contractId}`} style={{ display: 'inline-block', marginTop: 4, fontSize: '0.78rem', color: 'var(--link)' }}>
        {t('workOrders.testWindow.openCommissioning')}
      </a>
    </div>
  );
}

function DocumentsPanel({ workOrderId, serviceOrderId }: { workOrderId: number; serviceOrderId: number | null }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [signingId, setSigningId] = useState<number | null>(null);
  const isMxOrg = user?.organization_locale === 'MX';
  const canViewDocuments = can(user, 'signed_documents.view');
  const canSignDocuments = can(user, 'signed_documents.sign');

  const docsQ = useQuery({
    queryKey: ['wo-documents', workOrderId, serviceOrderId],
    queryFn: async () => {
      const query: Record<string, number> = serviceOrderId ? { service_order_id: serviceOrderId } : { work_order_id: workOrderId };
      const res = await (api.GET as unknown as (p: string, o: unknown) => Promise<{ data?: unknown; error?: unknown }>)(
        '/signed-documents', { params: { query } },
      );
      if (res.error) throw new Error('unavailable');
      return (res.data as { data: SignedDocSummary[] }).data;
    },
    enabled: canViewDocuments,
    retry: false,
  });

  if (!canViewDocuments) {
    return (
      <div style={{ padding: '0.5rem 1rem 0.75rem' }}>
        <strong style={{ fontSize: '0.85rem' }}>
          {t(isMxOrg ? 'workOrders.documents.mxTitle' : 'workOrders.documents.globalTitle')}
        </strong>
        <p style={{ margin: '6px 0 0', color: 'var(--text-secondary)', fontSize: '0.78rem' }}>
          {t('workOrders.documents.viewPermissionRequired')}
        </p>
      </div>
    );
  }
  if (docsQ.isError) return null;
  const docs = docsQ.data ?? [];
  if (!docs.length) return null;

  return (
    <div style={{ padding: '0.5rem 1rem 0.75rem' }}>
      <strong style={{ fontSize: '0.85rem' }}>
        {t(isMxOrg ? 'workOrders.documents.mxTitle' : 'workOrders.documents.globalTitle')}
      </strong>
      <ul style={{ listStyle: 'none', margin: '6px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {docs.map(d => (
          <li key={d.id} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: '0.82rem', padding: '5px 8px', background: 'var(--bg-subtle)', borderRadius: 6 }}>
            <span style={{ flex: 1, fontWeight: 500 }}>{d.title}</span>
            {d.status === 'signed' ? (
              <span style={{ color: 'var(--accent, #16a34a)' }}>✅ {t('workOrders.documents.signedBy', { name: d.signer_name ?? '' })}</span>
            ) : d.status === 'pending' ? (
              <>
                <span style={{ color: '#b45309' }}>{t('workOrders.documents.pending')}</span>
                {canSignDocuments ? (
                  <button style={{ ...styles.btnPrimary, padding: '3px 12px', fontSize: '0.78rem' }} onClick={() => setSigningId(d.id)}>
                    {t('workOrders.documents.openSign')}
                  </button>
                ) : (
                  <span style={{ color: 'var(--text-secondary)', fontSize: '0.76rem' }}>
                    {t('workOrders.documents.signPermissionRequired')}
                  </span>
                )}
              </>
            ) : (
              <span style={{ color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{d.status}</span>
            )}
          </li>
        ))}
      </ul>
      {signingId !== null && (
        <SignDocumentModal
          docId={signingId}
          onClose={() => setSigningId(null)}
          onSigned={() => void qc.invalidateQueries({ queryKey: ['wo-documents', workOrderId, serviceOrderId] })}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AcceptanceModal — install handoff readings (migration 445)
// ---------------------------------------------------------------------------
// Completing a contract-linked installation WO records ground truth at
// handoff: wireless signal / link rate, or FTTH optical Rx. One reading (or an
// explicit waive) is required — mirrors the backend gate so the tech learns it
// here, not from a 422.
function AcceptanceModal({ workOrder, onClose, onSubmit }: {
  workOrder: WorkOrder;
  onClose: () => void;
  onSubmit: (body: WorkOrderPatchBody) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [signal, setSignal] = useState('');
  const [link, setLink] = useState('');
  const [rx, setRx] = useState('');
  const [waived, setWaived] = useState(false);
  const [notes, setNotes] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const contractQ = useQuery({
    queryKey: ['wo-test-window-contract', workOrder.contract_id],
    queryFn: () => fetchWorkOrderContract(workOrder.contract_id!),
    enabled: Boolean(workOrder.contract_id),
    retry: false,
  });

  const submit = async () => {
    const body: WorkOrderPatchBody = { status: 'completed' };
    if (signal.trim() !== '') body.acceptance_signal_dbm = Number(signal);
    if (link.trim() !== '') body.acceptance_link_mbps = Number(link);
    if (rx.trim() !== '') body.acceptance_rx_dbm = Number(rx);
    if (waived) body.acceptance_waived = true;
    if (notes.trim() !== '') body.acceptance_notes = notes.trim();
    const hasReading = ['acceptance_signal_dbm', 'acceptance_link_mbps', 'acceptance_rx_dbm']
      .some(k => k in body);
    if (!hasReading && !waived) {
      setErr(t('workOrders.acceptance.needOne'));
      return;
    }
    setErr(null);
    setBusy(true);
    try {
      await onSubmit(body);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={modalStyles.backdrop} onClick={onClose}>
      <div style={{ ...modalStyles.panel, maxWidth: 460 }} onClick={e => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <h2 style={modalStyles.title}>{t('workOrders.acceptance.title')}</h2>
        </div>
        <div style={modalStyles.form}>
          <p style={{ margin: '0 0 0.25rem', fontWeight: 500 }}>{workOrder.title}</p>
          <p style={{ margin: '0 0 0.5rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
            {t('workOrders.acceptance.intro')}
          </p>
          <MxSandboxDocumentBanner environment={contractQ.data?.mx_contract_environment} />
          <label style={modalStyles.label}>
            {t('workOrders.acceptance.signal')}
            <input style={modalStyles.input} type="number" step="1" placeholder="-58"
              value={signal} onChange={e => setSignal(e.target.value)} />
          </label>
          <label style={modalStyles.label}>
            {t('workOrders.acceptance.link')}
            <input style={modalStyles.input} type="number" step="0.1" placeholder="87.5"
              value={link} onChange={e => setLink(e.target.value)} />
          </label>
          <label style={modalStyles.label}>
            {t('workOrders.acceptance.rx')}
            <input style={modalStyles.input} type="number" step="0.1" placeholder="-19.5"
              value={rx} onChange={e => setRx(e.target.value)} />
          </label>
          <label style={{ ...modalStyles.label, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={waived} onChange={e => setWaived(e.target.checked)} />
            {t('workOrders.acceptance.waive')}
          </label>
          <label style={modalStyles.label}>
            {t('workOrders.acceptance.notes')}
            <input style={modalStyles.input} maxLength={500}
              value={notes} onChange={e => setNotes(e.target.value)} />
          </label>
          {err && <p style={{ color: '#ef4444', fontSize: '0.85rem', margin: 0 }}>{err}</p>}
          <div style={modalStyles.actions}>
            <button style={styles.btnSecondary} onClick={onClose} disabled={busy}>
              {t('common.cancel')}
            </button>
            <button style={styles.btnPrimary} onClick={submit} disabled={busy}>
              {t('workOrders.acceptance.complete')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function WorkOrders() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [completing, setCompleting] = useState<WorkOrder | null>(null);
  // Row being edited (null = create mode) plus the prefill snapshot the PATCH
  // body is diffed against, so only user-touched fields are ever sent.
  const [editingRow, setEditingRow] = useState<WorkOrder | null>(null);
  const [initialForm, setInitialForm] = useState<Partial<WorkOrderBody>>({});
  const [form, setForm] = useState<Partial<WorkOrderBody>>({});
  const [formErr, setFormErr] = useState('');
  const sort = useTableSort('created_at', 'DESC');

  useEffect(() => { setPage(1); }, [sort.sortBy, sort.sortDir]);

  const workOrdersQ = useQuery({
    queryKey: ['workOrders', page, statusFilter, sort.sortBy, sort.sortDir],
    queryFn: () => fetchWorkOrders(page, statusFilter, sort.order_by, sort.order),
  });

  const sitesQ = useQuery({ queryKey: ['workOrders', 'siteOptions'], queryFn: () => fetchOptions('/sites') });
  const devicesQ = useQuery({ queryKey: ['workOrders', 'deviceOptions'], queryFn: () => fetchOptions('/devices') });
  const usersQ = useQuery({ queryKey: ['workOrders', 'assignableUsers'], queryFn: fetchAssignableUsers });

  // 'YYYY-MM-DDTHH:mm' (datetime-local input) → 'YYYY-MM-DD HH:mm:00' for the API.
  const toSqlDateTime = (v: string): string => v.replace('T', ' ') + (v.length === 16 ? ':00' : '');

  // PATCH only what the user actually changed. Untouched columns keep their
  // stored values (incl. seconds on timestamps and entity links), and an
  // assignee who has since lost authorization no longer blocks unrelated edits.
  const buildPatchBody = (): WorkOrderPatchBody => {
    const body: WorkOrderPatchBody = {};
    if ((form.title ?? '') !== (initialForm.title ?? '')) body.title = form.title;
    if ((form.description ?? '') !== (initialForm.description ?? '')) body.description = form.description || null;
    if ((form.status ?? 'pending') !== (initialForm.status ?? 'pending')) body.status = form.status ?? 'pending';
    if ((form.priority ?? 'medium') !== (initialForm.priority ?? 'medium')) body.priority = form.priority ?? 'medium';
    if ((form.work_type ?? 'other') !== (initialForm.work_type ?? 'other')) body.work_type = form.work_type ?? 'other';
    if ((form.scheduled_at ?? '') !== (initialForm.scheduled_at ?? '')) body.scheduled_at = form.scheduled_at ? toSqlDateTime(form.scheduled_at) : null;
    if ((form.client_id ?? null) !== (initialForm.client_id ?? null)) body.client_id = form.client_id ?? null;
    if ((form.site_id ?? null) !== (initialForm.site_id ?? null)) body.site_id = form.site_id ?? null;
    if ((form.device_id ?? null) !== (initialForm.device_id ?? null)) body.device_id = form.device_id ?? null;
    if ((form.assigned_to ?? null) !== (initialForm.assigned_to ?? null)) body.assigned_to = form.assigned_to ?? null;
    return body;
  };

  const saveMut = useMutation({
    mutationFn: (): Promise<void> => {
      if (editingRow !== null) {
        const body = buildPatchBody();
        // Nothing changed — close as a no-op instead of tripping the backend's
        // "No valid fields to update" 422.
        if (Object.keys(body).length === 0) return Promise.resolve();
        return patchWorkOrder(editingRow.id, body);
      }
      const createBody = { ...form } as WorkOrderBody;
      if (form.scheduled_at) createBody.scheduled_at = toSqlDateTime(form.scheduled_at);
      return createWorkOrder(createBody).then(() => undefined);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workOrders'] });
      setShowModal(false);
      setForm({});
      setInitialForm({});
      setEditingRow(null);
    },
    onError: (e: unknown) => setFormErr((e as { message?: string })?.message ?? 'Failed'),
  });

  const patchMut = useMutation({
    mutationFn: ({ id, body }: { id: number; body: WorkOrderPatchBody }) => patchWorkOrder(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workOrders'] }),
  });

  const totalPages = Math.ceil((workOrdersQ.data?.meta.total ?? 0) / PAGE_SIZE) || 1;

  const statusLabel = (s: string): string => {
    const map: Record<string, string> = {
      pending: t('workOrders.status.pending'),
      assigned: t('workOrders.status.assigned'),
      in_progress: t('workOrders.status.inProgress'),
      completed: t('workOrders.status.completed'),
      cancelled: t('workOrders.status.cancelled'),
    };
    return map[s] ?? s;
  };

  const workTypeLabel = (w: string | null): string =>
    w ? t(`workOrders.workType.${w}`, w) : t('workOrders.none');

  const targetLabel = (wo: WorkOrder): string =>
    wo.client_name || wo.site_name || wo.device_name || t('workOrders.none');

  const assigneeName = (wo: WorkOrder): string => {
    const name = `${wo.assigned_first ?? ''} ${wo.assigned_last ?? ''}`.trim();
    return name || t('workOrders.none');
  };

  const hasTarget = Boolean(form.client_id || form.site_id || form.device_id);

  const openCreate = () => {
    setEditingRow(null);
    setInitialForm({});
    setForm({});
    setFormErr('');
    setShowModal(true);
  };

  const openEdit = (wo: WorkOrder) => {
    // scheduled_at keeps its time-of-day: slice to the 'YYYY-MM-DDTHH:mm' shape
    // datetime-local expects (a date-only slice would reset schedules to
    // midnight on save).
    const prefill: Partial<WorkOrderBody> = {
      title: wo.title,
      description: wo.description ?? undefined,
      status: wo.status,
      priority: wo.priority ?? 'medium',
      work_type: wo.work_type ?? 'other',
      scheduled_at: wo.scheduled_at ? wo.scheduled_at.slice(0, 16) : undefined,
      client_id: wo.client_id ?? undefined,
      site_id: wo.site_id ?? undefined,
      device_id: wo.device_id ?? undefined,
      assigned_to: wo.assigned_to ?? undefined,
    };
    setEditingRow(wo);
    setInitialForm(prefill);
    setForm(prefill);
    setFormErr('');
    setShowModal(true);
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.pageTitle}>{t('workOrders.title')}</h1>
        <button style={styles.btnPrimary} onClick={openCreate}>
          {t('workOrders.new')}
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <select
          style={styles.input}
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
        >
          <option value="">All statuses</option>
          {STATUSES.map(s => (
            <option key={s} value={s}>{statusLabel(s)}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      {workOrdersQ.isLoading ? (
        <p>{t('common.loading')}</p>
      ) : workOrdersQ.error ? (
        <p style={{ color: '#dc2626' }}>{t('common.loadError')}</p>
      ) : (
        <>
          {(workOrdersQ.data?.data ?? []).length === 0 ? (
            <p style={{ color: 'var(--text-secondary)' }}>{t('workOrders.noOrders')}</p>
          ) : (
            <div style={styles.tableCard}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <SortableTh label={t('common.id')} col="id" sort={sort} style={styles.th} />
                    <SortableTh label="Title" col="title" sort={sort} style={styles.th} />
                    <SortableTh label={t('workOrders.type')} col="work_type" sort={sort} style={styles.th} />
                    {/* target is a derived label from joined client/site/device name — non-sortable */}
                    <th style={styles.th}>{t('workOrders.target')}</th>
                    {/* assigned_to name comes from a JOIN on users — non-sortable by name; assigned_to FK is own-table */}
                    <th style={styles.th}>Assigned To</th>
                    <SortableTh label="Status" col="status" sort={sort} style={styles.th} />
                    <SortableTh label="Scheduled" col="scheduled_at" sort={sort} style={styles.th} />
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(workOrdersQ.data?.data ?? []).map(wo => (
                    <>
                      <tr key={wo.id} style={{ cursor: 'pointer' }} onClick={() => setExpandedId(v => v === wo.id ? null : wo.id)}>
                        <td style={styles.td}>{wo.id}</td>
                        <td style={styles.td}>{wo.title}</td>
                        <td style={{ ...styles.td, textTransform: 'capitalize' }}>{workTypeLabel(wo.work_type)}</td>
                        <td style={styles.td}>{targetLabel(wo)}</td>
                        <td style={styles.td}>{assigneeName(wo)}</td>
                        <td style={styles.td}><StatusBadge status={wo.status} /></td>
                        <td style={styles.td}>{wo.scheduled_at ? wo.scheduled_at.slice(0, 10) : t('common.na')}</td>
                        <td style={styles.td} onClick={e => e.stopPropagation()}>
                          <button
                            style={{ ...styles.btnSecondary, marginRight: 4 }}
                            onClick={() => openEdit(wo)}
                          >
                            {t('common.edit', 'Edit')}
                          </button>
                          {wo.status === 'pending' && (
                            <button
                              style={{ ...styles.btnPrimary, marginRight: 4 }}
                              onClick={() => patchMut.mutate({ id: wo.id, body: { status: 'assigned' } })}
                            >
                              Dispatch
                            </button>
                          )}
                          {wo.status === 'assigned' && (
                            <button
                              style={{ ...styles.btnPrimary, marginRight: 4 }}
                              onClick={() => patchMut.mutate({ id: wo.id, body: { status: 'in_progress' } })}
                            >
                              Start
                            </button>
                          )}
                          {wo.status === 'in_progress' && (
                            <>
                              {/* Pickup orders complete themselves once every
                                  outstanding unit below is resolved — a blind
                                  status flip here would leave equipment
                                  unaccounted for, so no manual Complete button. */}
                              {wo.work_type !== 'pickup' && (
                                <button
                                  style={{ ...styles.btnPrimary, marginRight: 4 }}
                                  onClick={() => {
                                    // Contract-linked installs need acceptance
                                    // readings — the backend 422s a blind flip.
                                    if (wo.work_type === 'installation' && wo.contract_id) setCompleting(wo);
                                    else patchMut.mutate({ id: wo.id, body: { status: 'completed' } });
                                  }}
                                >
                                  Complete
                                </button>
                              )}
                              <button
                                style={styles.btnDanger}
                                onClick={() => patchMut.mutate({ id: wo.id, body: { status: 'cancelled' } })}
                              >
                                Cancel
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                      {expandedId === wo.id && (
                        <tr key={`${wo.id}-materials`}>
                          <td colSpan={8} style={{ padding: 0 }}>
                            {wo.work_type === 'installation' && wo.contract_id !== null && (
                              <TestWindowPanel workOrderId={wo.id} contractId={wo.contract_id} />
                            )}
                            {wo.work_type === 'installation' && (
                              <DocumentsPanel workOrderId={wo.id} serviceOrderId={wo.service_order_id} />
                            )}
                            {wo.work_type === 'pickup'
                              ? <PickupChecklistPanel workOrderId={wo.id} />
                              : <MaterialsPanel workOrderId={wo.id} />}
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          <div style={styles.pagination}>
            <button style={styles.btnSecondary} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              {t('common.prev')}
            </button>
            <span>{page} / {totalPages}</span>
            <button style={styles.btnSecondary} disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
              {t('common.next')}
            </button>
          </div>
        </>
      )}

      {/* Install-acceptance completion modal */}
      {completing && (
        <AcceptanceModal
          workOrder={completing}
          onClose={() => setCompleting(null)}
          onSubmit={async (body) => {
            await patchMut.mutateAsync({ id: completing.id, body });
            setCompleting(null);
          }}
        />
      )}

      {/* Create modal */}
      {showModal && (
        <div style={modalStyles.backdrop} onClick={() => setShowModal(false)}>
          <div style={modalStyles.panel} onClick={e => e.stopPropagation()}>
            <div style={modalStyles.header}>
              <h2 style={modalStyles.title}>
                {editingRow !== null ? t('workOrders.edit', 'Edit Work Order') : t('workOrders.new')}
              </h2>
            </div>
            <div style={modalStyles.form}>
              <label style={modalStyles.label}>
                Title *
                <input
                  style={modalStyles.input}
                  value={form.title ?? ''}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                />
              </label>
              <label style={modalStyles.label}>
                Description
                <textarea
                  style={{ ...modalStyles.input, height: 80 }}
                  value={form.description ?? ''}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                />
              </label>
              <label style={modalStyles.label}>
                Status
                <select
                  style={modalStyles.select}
                  value={form.status ?? 'pending'}
                  onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                >
                  {STATUSES.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
                </select>
              </label>
              <label style={modalStyles.label}>
                Priority
                <select
                  style={modalStyles.select}
                  value={form.priority ?? 'medium'}
                  onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
                >
                  {PRIORITIES.map(p => (
                    <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                  ))}
                </select>
              </label>
              <label style={modalStyles.label}>
                {t('workOrders.type')}
                <select
                  style={modalStyles.select}
                  value={form.work_type ?? 'other'}
                  onChange={e => setForm(f => ({ ...f, work_type: e.target.value }))}
                >
                  {WORK_TYPES.map(w => <option key={w} value={w}>{workTypeLabel(w)}</option>)}
                </select>
              </label>

              {/* Target — a work order links to AT LEAST ONE of client / site /
                  device (none is individually required; client is not mandatory). */}
              <div style={{ marginTop: '0.25rem' }}>
                <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)' }}>{t('workOrders.target')}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{t('workOrders.targetRequired')}</div>
              </div>
              <ClientPicker
                required={false}
                value={form.client_id ?? ''}
                initialName={editingRow?.client_name ?? ''}
                onChange={(id) => setForm(f => ({ ...f, client_id: id || undefined }))}
              />
              <label style={modalStyles.label}>
                {t('workOrders.site')}
                <select
                  style={modalStyles.select}
                  value={form.site_id ?? ''}
                  onChange={e => setForm(f => ({ ...f, site_id: e.target.value ? Number(e.target.value) : undefined }))}
                >
                  <option value="">{t('workOrders.none')}</option>
                  {/* Keep the row's current site selectable even when it falls
                      outside the 200-row options fetch */}
                  {editingRow && form.site_id != null && !(sitesQ.data ?? []).some(s => s.id === form.site_id) && (
                    <option value={form.site_id}>{editingRow.site_name ?? `#${form.site_id}`}</option>
                  )}
                  {(sitesQ.data ?? []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </label>
              <label style={modalStyles.label}>
                {t('workOrders.device')}
                <select
                  style={modalStyles.select}
                  value={form.device_id ?? ''}
                  onChange={e => setForm(f => ({ ...f, device_id: e.target.value ? Number(e.target.value) : undefined }))}
                >
                  <option value="">{t('workOrders.none')}</option>
                  {editingRow && form.device_id != null && !(devicesQ.data ?? []).some(d => d.id === form.device_id) && (
                    <option value={form.device_id}>{editingRow.device_name ?? `#${form.device_id}`}</option>
                  )}
                  {(devicesQ.data ?? []).map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </label>

              <label style={modalStyles.label}>
                Scheduled
                <input
                  type="datetime-local"
                  style={modalStyles.input}
                  value={form.scheduled_at ?? ''}
                  onChange={e => setForm(f => ({ ...f, scheduled_at: e.target.value || undefined }))}
                />
              </label>

              <label style={modalStyles.label}>
                Assigned To
                <select
                  style={modalStyles.select}
                  value={form.assigned_to ?? ''}
                  onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value ? Number(e.target.value) : undefined }))}
                >
                  <option value="">Unassigned</option>
                  {/* The row's current assignee may no longer be authorized (or
                      active) and thus absent from the options — keep them
                      visible and selectable rather than misrendering as
                      Unassigned. The diff-based PATCH means leaving them in
                      place never trips the backend's assignee guard. */}
                  {editingRow && form.assigned_to != null && !(usersQ.data ?? []).some(u => u.id === form.assigned_to) && (
                    <option value={form.assigned_to}>{assigneeName(editingRow)} ({t('workOrders.current', 'current')})</option>
                  )}
                  {(usersQ.data ?? []).map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                </select>
              </label>
              {formErr && <p style={modalStyles.error}>{formErr}</p>}
              <div style={modalStyles.actions}>
                <button style={styles.btnSecondary} onClick={() => setShowModal(false)}>{t('common.cancel')}</button>
                <button
                  style={styles.btnPrimary}
                  disabled={!form.title || !hasTarget || saveMut.isPending}
                  onClick={() => { setFormErr(''); saveMut.mutate(); }}
                >
                  {saveMut.isPending ? t('common.saving') : t('common.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
